import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchGroups,
  patchBedOccupant,
  patchFieldValue,
  patchGuardian,
  patchJobAssignment,
  patchMember,
  patchProgramAssignment,
  postBedOccupant,
  postFieldValue,
  postGroupParticipant,
  postJobAssignment,
  postProgramAssignment,
} from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import Input from "../../../ui/Input";
import InputPhone from "../../../ui/InputPhone";
import SelectObject from "../../../ui/SelectObject";
import useApp from "../../../../utils/useApp";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import CustomField from "../../../ui/CustomField";
import CaptureProfilePic from "../../../ui/CaptureProfilePic";
import { UserFields } from "../../../../types/userFields";
import { validateUserFields } from "../../../../helpers/validation";
import FormCheck from "../../../ui/FormCheckbox";
import { faIdCard, faShieldHalved, faUsersLine } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GroupCard from "./GroupCard";
import MemberHeader from "./MemberHeader";

interface EditMemberState {
  user?: any;
  possiblePrimaryContacts: any[];
  currentCase?: any;
  relationships: any[];
  genders: any[];
  ethnicities: any[];
  races: any[];
  originalPrograms: any[];
  originalJobs: any[];
  originalRooms: any[];
  originalBeds: any[];
  myCase: boolean;
  setUserToSave: any;
  onClickDoneEdit: (forceNoModal?: boolean) => void;
  setIsSaving: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const EditMember = ({
  user,
  possiblePrimaryContacts,
  currentCase,
  relationships,
  genders,
  ethnicities,
  races,
  originalPrograms,
  originalJobs,
  originalRooms,
  originalBeds,
  myCase,
  setUserToSave,
  onClickDoneEdit,
  setIsSaving,
  setShowToast,
  setToastText,
  setToastBody,
}: EditMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const [isPrimaryUpdated, setIsPrimaryUpdated] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [profileImage, setProfileImage] = useState<string>("");
  const [profileData, setProfileData] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [userUpdated, setUserUpdated] = useState<boolean>(false);
  const [customUpdated, setCustomUpdated] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [prevProgram, setPrevProgram] = useState<any>();
  const [prevJob, setPrevJob] = useState<any>();
  const [prevBed, setPrevBed] = useState<any>();
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDemo, setShowDemo] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [userFields, setUserFields] = useState<UserFields>({
    type: "",
    firstName: "",
    lastName: "",
    preferredName: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    groups: [],
    isPrimary: false,
    isPersonServed: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });

  const getPrograms = () => {
    let programs = originalPrograms;
    const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
    setPrograms(updatedPrograms);
    setLevels([]);

    const selectedProgram = updatedPrograms.find((p: any) => p.key === user?.program?.program_key);
    if (selectedProgram) {
      if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
        setLevels(selectedProgram.programLevels);
        if (user?.program?.level_id) {
          const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.program?.level_id);
          if (selectedLevel) {
            setUserFields((prev) => ({ ...prev, level: selectedLevel }));
          } else {
            setUserFields((prev) => ({ ...prev, level: null }));
          }
        }
      }
      setUserFields((prev) => ({ ...prev, program: selectedProgram }));
    }
    setPrevProgram(user?.program);
    setSaveIsEnabled(false);
    setUserToSave(false);
  };

  const getJobs = async () => {
    const jobs = originalJobs;
    var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

    //Ensure user's job is included
    const userJob = jobs.find((j: any) => j.key === user?.job?.key);

    if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
      availJobs.push(userJob);
    }

    availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
    setJobs(availJobs);

    const selectedJob = availJobs.find((j: any) => j.key === user?.job?.key);
    if (selectedJob) {
      setUserFields((prev) => ({ ...prev, job: selectedJob }));
    } else if (user?.job) {
      const currentJob = jobs.find((j: any) => j.key === user?.job?.key);
      setUserFields((prev) => ({ ...prev, job: currentJob }));
      setJobs((prevJobs) => [currentJob, ...prevJobs]);
    }
    setPrevJob(user?.job);
  };

  const getBeds = () => {
    var bedList = originalBeds;
    //include bed user is currently assigned to
    var currentBed;
    if (user?.bed) {
      currentBed = bedList.find((b: any) => b.key === user?.bed?.key);
    }
    //filter to vacant beds
    bedList = bedList.filter((b: any) => b.occupant_id === null);

    if (currentBed) {
      //if user is assigned to a bed, add it to the list
      bedList.push(currentBed);
    }

    bedList.sort((a: any, b: any) => {
      // group by room name
      if (a.room_name !== b.room_name) {
        return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
      }
      // sort beds in each room alphanumerically
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });

    setBeds(bedList);
    if (user?.room) {
      const filtered = bedList.filter((b: any) => b.room_key === user?.room);
      setFilteredBeds(filtered);
    } else {
      setFilteredBeds(bedList);
    }
  };

  const getRooms = () => {
    const roomList = originalRooms;
    roomList.sort((a: any, b: any) => {
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });
    setRooms(roomList);
    setPrevBed(user?.bed);
  };

  const getGroups = () => {
    fetchGroups()
      .then((res: any) => {
        const groupList = res[0].data;
        groupList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setGroups(groupList);
        const userGroupIds = new Set(user?.groups.map((g: any) => g.id));
        const available = groupList.filter((g: any) => !userGroupIds.has(g.id));
        setAvailableGroups(available);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);

    setUserFields({
      type: user.type ?? "",
      firstName: user.firstname ?? "",
      lastName: user.lastname ?? "",
      preferredName: user.preferredname ?? "",
      email: user.email ?? "",
      phone: user.phonenumber === "" ? null : user.phonenumber,
      intakeDate: user.intake_at,
      exitDate: user.exit_at,
      birthDate: user.birthdate,
      relationship: user.relationship ?? "",
      gender: user.gender ?? "",
      ethnicity: user?.ethnicity ?? "",
      race: user.race ?? "",
      school: user.school ?? "",
      grade: user.grade ?? "",
      ...(auth?.profile.includeHMIS === 1 && {
        room: user.room ?? "",
        bed: user.bed ?? null,
      }),
      groups: user.groups ?? [],
      isPrimary: user.is_primary === 1,
      isPersonServed: user.is_case_subject === 1,
      appAccess: user?.appaccess === 1 ? true : false,
      appAccessLast: user.appaccess_last_at,
      isBanned: user.is_banned === 1,
    });

    if (user.profile.length > 0) {
      user?.profile?.map((p: any) => {
        setProfileData(p?.data);
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileData("");
      setProfileImage("");
    }

    if (user.customfields) {
      setCustom(user.customfields);
    }

    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
      getRooms();
    }
    getGroups();
    setIsLoading(false);
  }, [user, auth]);

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
    fieldType: string = "user",
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    if (setUserToSave) setUserToSave(true);
    if (fieldType === "custom") {
      setCustomUpdated(true);
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      if (!["program", "level", "job", "bed", "room"].includes(field)) {
        setUserUpdated(true);
      }
      return updated;
    });
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.key);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.key);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("bed", e);
  };

  const updateGroup = (e: any) => {
    setSelectedGroup(e);
  };

  const updateIsPrimary = () => {
    if (!userFields.isPrimary) {
      setIsPrimaryUpdated(true);
    } else {
      setIsPrimaryUpdated(false);
    }
    updateField("isPrimary", !userFields.isPrimary);
  };

  const saveMember = () => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    setErrMsg("");

    if (validation.isValid) {
      setIsSaving(true);

      if (user?.type === "guardian") {
        if (isPrimaryUpdated) {
          const otherPrimaryContacts = possiblePrimaryContacts.filter((g: any) => {
            return g.id !== user?.id;
          });

          const otherUpdatedGuardian = {
            is_primary: false,
          };

          otherPrimaryContacts.map((o: any) => {
            patchGuardian(otherUpdatedGuardian, id, o?.id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          });
        }

        if (userUpdated) {
          var updatedUser: any = {
            firstname: userFields.firstName,
            lastname: userFields.lastName,
            preferredname: userFields.preferredName,
            is_primary: userFields.isPrimary,
            is_case_subject: userFields.isPersonServed,
            email: userFields.email,
            phonenumber: userFields.phone === "1" ? null : userFields.phone,
            relationship: userFields.relationship,
            intake_at: userFields.intakeDate,
            exit_at: userFields.exitDate,
            birthdate: userFields.birthDate,
            gender: userFields.gender,
            ethnicity: userFields.ethnicity,
            race: userFields.race,
          };

          const prevAppAccess = user?.appaccess === 1 ? true : false;
          if (prevAppAccess !== userFields.appAccess) {
            updatedUser.appaccess = userFields.appAccess;
          }

          //update user
          patchGuardian(updatedUser, id, user?.id)
            .then((res: any) => {})
            .catch((err: any) => {
              setErrMsg(err?.response?.data?.msg);
            });
        }

        //job has changed
        if (prevJob?.key !== userFields.job?.key) {
          //remove from previous job
          if (prevJob?.job_assignment_id) {
            const previousJob = {
              user_id: user?.id,
            };

            patchJobAssignment(previousJob, id, prevJob?.job_assignment_id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
          //new job
          if (userFields.job && userFields.job?.id !== 0) {
            const newJob = {
              job_id: userFields.job.id,
              user_id: user?.id,
            };

            postJobAssignment(newJob, id)
              .then((res: any) => {
                const response = res[0].data;
                const newJob = {
                  job_assignment_id: response?.job_assignment_id,
                  job_id: userFields.job?.id,
                  key: userFields.job?.key,
                  name: userFields.job?.name,
                };
                setPrevJob(newJob);
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }

        //program or level has changed
        if (
          prevProgram?.program_key !== userFields.program?.key ||
          (userFields.level && prevProgram?.level_key !== userFields.level?.key)
        ) {
          //remove from previous program level
          if (prevProgram?.program_assignment_id) {
            const previousProgramLevel = {
              user_id: user?.id,
            };

            patchProgramAssignment(previousProgramLevel, id, prevProgram?.program_assignment_id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
          //new program or level
          if (userFields.program && userFields.program?.id !== 0) {
            let newProgram: any = {
              program_id: userFields.program?.id,
              user_id: user?.id,
            };

            if (userFields.level) {
              newProgram = { ...newProgram, level_id: userFields.level?.id };
            }

            postProgramAssignment(newProgram, id)
              .then((res: any) => {
                const response = res[0].data;
                const newPrevious = {
                  program_assignment_id: response?.program_assignment_id,
                  program_id: userFields.program?.id,
                  program_key: userFields.program?.key,
                  program_name: userFields.program?.name,
                  level_id: userFields.level?.id,
                  level_key: userFields.level?.key,
                  level_name: userFields.level?.name,
                };
                setPrevProgram(newPrevious);
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }

        if (auth?.profile.includeHMIS === 1) {
          //bed changed
          if (prevBed?.key !== userFields.bed?.key) {
            //remove from previous bed
            if (prevBed?.bed_occupant_id) {
              patchBedOccupant(id, prevBed?.bed_occupant_id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
            //new bed
            if (userFields.bed) {
              const newBed = {
                bed_id: userFields.bed?.id,
                user_id: user?.id,
              };

              postBedOccupant(newBed, id)
                .then((res: any) => {
                  const response = res[0].data;
                  const newBed = {
                    bed_occupant_id: response?.bed_occupant_id,
                    id: userFields.bed?.id,
                    key: userFields.bed?.key,
                    room_id: userFields.bed?.room_id,
                    room_key: userFields.bed?.room_key,
                  };
                  setPrevBed(newBed);
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
          }
        }

        //update / create custom field values
        if (customUpdated) {
          custom.map((cust: any) => {
            if (cust?.field_value_id) {
              //patch
              const fieldVal = {
                value: cust?.value,
              };

              patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            } else if (cust?.value) {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: user?.id,
                person_type: "guardian",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            }
          });
        }
      } else {
        if (userUpdated) {
          const updatedMember: any = {
            firstname: userFields.firstName,
            lastname: userFields.lastName,
            preferredname: userFields.preferredName,
            phonenumber: userFields.phone === "1" ? null : userFields.phone,
            intake_at: userFields.intakeDate,
            exit_at: userFields.exitDate,
            birthdate: userFields.birthDate,
            relationship: userFields.relationship,
            gender: userFields.gender,
            ethnicity: userFields.ethnicity,
            race: userFields.race,
            school: userFields.school,
            grade: userFields.grade,
            is_case_subject: userFields.isPersonServed,
          };

          patchMember(updatedMember, id, user?.id)
            .then((res: any) => {})
            .catch((err: any) => {
              setErrMsg(err?.response?.data?.msg);
            });
        }

        //job has changed
        if (prevJob?.key !== userFields.job?.key) {
          //remove from previous job
          if (prevJob?.job_assignment_id) {
            const previousJob = {
              member_id: user?.id,
            };

            patchJobAssignment(previousJob, id, prevJob?.job_assignment_id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
          //new job
          if (userFields.job && userFields.job?.id !== 0) {
            const newJob = {
              job_id: userFields.job.id,
              member_id: user?.id,
            };

            postJobAssignment(newJob, id)
              .then((res: any) => {
                const response = res[0].data;
                const newJob = {
                  job_assignment_id: response?.job_assignment_id,
                  job_id: userFields.job?.id,
                  key: userFields.job?.key,
                  name: userFields.job?.name,
                };
                setPrevJob(newJob);
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }

        //program or level has changed
        if (
          prevProgram?.program_key !== userFields.program?.key ||
          (userFields.level && prevProgram?.level_key !== userFields.level?.key)
        ) {
          //remove from previous program level
          if (prevProgram?.program_assignment_id) {
            const previousProgramLevel = {
              member_id: user?.id,
            };

            patchProgramAssignment(previousProgramLevel, id, prevProgram?.program_assignment_id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
          //new program or level
          if (userFields.program && userFields.program?.id !== 0) {
            let newProgram: any = {
              program_id: userFields.program?.id,
              member_id: user?.id,
            };

            if (userFields.level) {
              newProgram = { ...newProgram, level_id: userFields.level?.id };
            }

            postProgramAssignment(newProgram, id)
              .then((res: any) => {
                const response = res[0].data;
                const newPrevious = {
                  program_assignment_id: response?.program_assignment_id,
                  program_id: userFields.program?.id,
                  program_key: userFields.program?.key,
                  program_name: userFields.program?.name,
                  level_id: userFields.level?.id,
                  level_key: userFields.level?.key,
                  level_name: userFields.level?.name,
                };
                setPrevProgram(newPrevious);
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }

        if (auth?.profile.includeHMIS === 1) {
          //bed changed
          if (prevBed?.key !== userFields.bed?.key) {
            //remove from previous bed
            if (prevBed?.bed_occupant_id) {
              patchBedOccupant(id, prevBed?.bed_occupant_id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
            //new bed
            if (userFields.bed) {
              const newBed = {
                bed_id: userFields.bed?.id,
                member_id: user?.id,
              };

              postBedOccupant(newBed, id)
                .then((res: any) => {
                  const response = res[0].data;
                  const newBed = {
                    bed_occupant_id: response?.bed_occupant_id,
                    id: userFields.bed?.id,
                    key: userFields.bed?.key,
                    room_id: userFields.bed?.room_id,
                    room_key: userFields.bed?.room_key,
                  };
                  setPrevBed(newBed);
                })
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
          }
        }

        //update / create custom field values
        if (customUpdated) {
          custom.map((cust: any) => {
            if (cust?.field_value_id) {
              //patch
              const fieldVal = {
                value: cust?.value,
              };

              patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            } else if (cust?.value) {
              //post
              const fieldVal = {
                field_id: cust?.field_id,
                person_id: user?.id,
                person_type: "member",
                value: cust?.value,
              };

              postFieldValue(fieldVal).catch((err: any) => {
                console.log(err);
                setErrMsg(err?.response?.data?.msg);
              });
            }
          });
        }
      }

      setUserUpdated(false);
      setCustomUpdated(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
      setShowToast(true);
      setToastText(user?.type === "guardian" ? "Primary Adult Updated" : "Member Updated");
      const toast = userFields.preferredName
        ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
        : userFields.firstName + " " + userFields.lastName;
      setToastBody(toast);

      setTimeout(() => {
        onClickDoneEdit(true);
        setIsSaving(false);
      }, 3000);
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const onClickAddGroup = () => {
    const newParticipant = {
      participant_id: user?.id,
      participant_type: user?.type,
    };

    postGroupParticipant(newParticipant, selectedGroup?.id)
      .then((res: any) => {
        setUserFields((prev) => {
          const participantId = res[0].data.group_participant_id;
          const addedGroup = { ...selectedGroup, group_participant_id: participantId };

          const updatedGroups = [...(prev?.groups ?? []), addedGroup];
          const userGroupIds = new Set(updatedGroups.map((g: any) => g.id));
          const available = groups.filter((g: any) => !userGroupIds.has(g.id));
          setAvailableGroups(available);

          const updated = { ...prev, groups: updatedGroups };
          return updated;
        });

        setSelectedGroup(null);
        setShowToast(true);
        setToastText("Group Added");
        setToastBody(selectedGroup?.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const updateSelection = (type: string) => {
    switch (type) {
      case "demo":
        setShowDemo(true);
        setShowCustom(false);
        setShowAssign(false);
        break;
      case "custom":
        setShowDemo(false);
        setShowCustom(true);
        setShowAssign(false);
        break;
      case "assign":
        setShowDemo(false);
        setShowCustom(false);
        setShowAssign(true);
        break;
      default:
        setShowDemo(true);
        setShowCustom(false);
        setShowAssign(false);
        break;
    }
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <div>
          <Stack gap={2} className="mb-3">
            <Row>
              <Col xs={12} lg={8}>
                <MemberHeader user={user} profileImage={profileImage} />
              </Col>
              {!user?.deleted_at && (
                <Col xs={12} lg={4}>
                  <Stack className="stack-float" direction="horizontal" gap={3}>
                    <Button className="app-secondary-color button" onClick={() => saveMember()} disabled={!saveIsEnabled}>
                      Save
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                      Cancel
                    </Button>
                  </Stack>
                </Col>
              )}
            </Row>
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            <Row className="my-3">
              <Col xs={4} className="d-grid">
                <Button
                  className={showDemo ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                  onClick={() => updateSelection("demo")}
                >
                  <FontAwesomeIcon icon={faIdCard} className={"me-2"} />
                  {"Demographics"}
                </Button>
              </Col>
              <Col xs={4} className="d-grid">
                <Button
                  className={showCustom ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                  onClick={(e) => updateSelection("custom")}
                >
                  <FontAwesomeIcon icon={faShieldHalved} className={"me-2"} />
                  {"Custom Fields"}
                </Button>
              </Col>
              <Col xs={4} className="d-grid">
                <Button
                  className={showAssign ? "app-primary-color button button-toggle" : "app-primary-color-inverse button button-toggle"}
                  onClick={(e) => updateSelection("assign")}
                >
                  <FontAwesomeIcon icon={faUsersLine} className={"me-2"} />
                  {"Program and Assignment"}
                </Button>
              </Col>
            </Row>

            {showDemo && (
              <>
                <Row className="h-auto">
                  <Col xs={12} md={6} lg={4}>
                    <Input
                      label="First Name"
                      type="text"
                      value={userFields.firstName}
                      onUpdate={(e: any) => updateField("firstName", e.target.value)}
                      required={true}
                      css={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <Input
                      label="Preferred Name"
                      type="text"
                      value={userFields.preferredName}
                      onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                      css={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <Input
                      label="Last Name"
                      type="text"
                      value={userFields.lastName}
                      onUpdate={(e: any) => updateField("lastName", e.target.value)}
                      required={true}
                      css={"mb-3"}
                    />
                  </Col>
                  {user?.type === "guardian" && (
                    <Col xs={12} md={6} lg={4}>
                      <Input
                        label="Email"
                        type="text"
                        value={userFields.email}
                        onUpdate={(e: any) => updateField("email", e.target.value)}
                        required={userFields.appAccess}
                        css={"mb-3"}
                      />
                    </Col>
                  )}
                  <Col xs={12} md={6} lg={4}>
                    <InputPhone label="Phone" value={userFields.phone} onUpdate={(e: any) => updateField("phone", e)} css={"mb-3"} />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <DatePicker
                      label="Birth Date"
                      currentValue={userFields.birthDate}
                      setCurrentValue={(e: any) => updateField("birthDate", e)}
                      hasMaxDate={true}
                      disabled={myCase ? false : true}
                      css={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <SelectObject
                      label="Gender"
                      value={userFields.gender}
                      options={genders}
                      onUpdate={(e: any) => updateField("gender", e.target.value)}
                      disabled={myCase ? false : true}
                      cssClasses={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <SelectObject
                      label="Ethnicity"
                      value={userFields.ethnicity}
                      options={ethnicities}
                      onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                      disabled={myCase ? false : true}
                      cssClasses={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <SelectObject
                      label="Race"
                      value={userFields.race}
                      options={races}
                      onUpdate={(e: any) => updateField("race", e.target.value)}
                      disabled={myCase ? false : true}
                      cssClasses={"mb-3"}
                    />
                  </Col>
                  {userFields.relationship === "child" && (
                    <>
                      <Col xs={12} md={6} lg={4}>
                        <Input
                          label="School"
                          type="text"
                          value={userFields.school}
                          onUpdate={(e: any) => updateField("school", e.target.value)}
                          disabled={myCase ? false : true}
                          css={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} md={6} lg={4}>
                        <Input
                          label="Grade"
                          type="text"
                          value={userFields.grade}
                          onUpdate={(e: any) => updateField("grade", e.target.value)}
                          disabled={myCase ? false : true}
                          css={"mb-3"}
                        />
                      </Col>
                    </>
                  )}
                  <Col xs={12} md={6} lg={4}>
                    <SelectObject
                      label="Relationship"
                      value={userFields.relationship}
                      options={relationships}
                      onUpdate={(e: any) => updateField("relationship", e.target.value)}
                      disabled={myCase ? false : true}
                      cssClasses={"mb-3"}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4}>
                    <DatePicker
                      label="Intake Date"
                      currentValue={userFields.intakeDate}
                      setCurrentValue={(e: any) => updateField("intakeDate", e)}
                      hasMaxDate={true}
                      disabled={currentCase?.casestatus === "active" && myCase ? false : true}
                      css={"mb-3"}
                    />
                  </Col>
                  {userFields?.exitDate && (
                    <Col xs={12} md={6} lg={4}>
                      <DatePicker
                        label="Exit Date"
                        currentValue={userFields.exitDate}
                        setCurrentValue={(e: any) => updateField("exitDate", e)}
                        hasMaxDate={true}
                        disabled={true}
                        css={"mb-3"}
                      />
                    </Col>
                  )}
                </Row>
                {showDemo && (
                  <Row>
                    <Col xs={4}>
                      <Stack gap={2}>
                        {user?.type === "guardian" && (
                          <>
                            <FormCheck label="Main Contact" value={userFields.isPrimary} onUpdate={(e: any) => updateIsPrimary()} />
                            <FormCheck
                              label="Person Served"
                              value={userFields.isPersonServed}
                              onUpdate={(e: any) => updateField("isPersonServed", !userFields.isPersonServed)}
                            />
                            <div>
                              <FormCheck
                                label="Mobile App Access"
                                value={userFields.appAccess}
                                onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                                css={"mb-0"}
                              />
                              <small>{"(Requires email or phone)"}</small>
                            </div>
                          </>
                        )}
                      </Stack>
                    </Col>
                    <Col xs={8}>
                      <CaptureProfilePic
                        userId={user?.id}
                        userProfileId={user?.profile_id}
                        userName={userFields.firstName + " " + userFields.lastName}
                        userType={user?.type}
                        myCase={myCase}
                        profileImage={profileImage}
                        setProfileImage={setProfileImage}
                        setProfileData={setProfileData}
                        setErrMsg={setErrMsg}
                        setShowToast={setShowToast}
                        setToastText={setToastText}
                        setToastBody={setToastBody}
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}
            {showCustom && user?.customfields?.length > 0 && (
              <>
                <Row className="h-auto">
                  {user?.customfields?.map((c: any) => (
                    <CustomField
                      customField={c}
                      setCustom={setCustom}
                      userId={user?.id}
                      userType={user?.type}
                      enableSave={() =>
                        validateUser(
                          userFields.firstName,
                          userFields.lastName,
                          userFields.email,
                          userFields.phone,
                          userFields.level,
                          levels.length > 0,
                          false,
                          "custom",
                        )
                      }
                      setSaveIsEnabled={setSaveIsEnabled}
                      myCase={myCase}
                      editMode={true}
                    />
                  ))}
                </Row>
              </>
            )}
            {showAssign && (
              <>
                <Row>
                  {programs.length > 0 && (
                    <Row>
                      {programs.length > 0 && (
                        <Col xs={12} lg={6}>
                          <SelectObjectTypeahead
                            label="Program"
                            value={userFields.program?.key}
                            options={programs}
                            onUpdate={(e: any) => updateProgram(e)}
                            disabled={userFields.isBanned ? true : myCase ? false : true}
                            cssClasses={"mb-3"}
                          />
                        </Col>
                      )}
                      {levels.length > 0 && (
                        <Col xs={12} lg={6}>
                          <SelectObjectTypeahead
                            label="Program Level"
                            value={userFields.level?.key}
                            options={levels}
                            onUpdate={(e: any) => updateLevel(e)}
                            disabled={userFields.isBanned ? true : myCase ? false : true}
                            cssClasses={"mb-3"}
                          />
                        </Col>
                      )}
                    </Row>
                  )}
                  {rooms.length > 0 && beds.length > 0 && (
                    <Row>
                      <Col xs={12} lg={6}>
                        <SelectObjectTypeahead
                          label="Room"
                          value={userFields.room}
                          options={rooms}
                          onUpdate={(e: any) => updateRoom(e)}
                          disabled={userFields.isBanned ? true : myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                      <Col xs={12} lg={6}>
                        <SelectObjectTypeahead
                          label="Bed"
                          value={userFields.bed?.key}
                          options={filteredBeds}
                          onUpdate={(e: any) => updateBed(e)}
                          disabled={userFields.room ? (myCase ? false : true) : true}
                          initialOption="Vacant"
                          cssClasses={"mb-3"}
                        />
                      </Col>
                    </Row>
                  )}
                  {jobs.length > 0 && (
                    <Row>
                      <Col xs={12} lg={6}>
                        <SelectObjectTypeahead
                          label="Job"
                          value={userFields.job?.key}
                          options={jobs}
                          onUpdate={(e: any) => updateJob(e)}
                          disabled={userFields.isBanned ? true : myCase ? false : true}
                          cssClasses={"mb-3"}
                        />
                      </Col>
                    </Row>
                  )}
                </Row>
                <h5 className="app-header-text mb-0">Groups</h5>
                <Row className="align-items-start">
                  {userFields?.groups && userFields?.groups?.length > 0 && (
                    <Col xs={12} lg={5}>
                      <Stack>
                        {userFields?.groups.map((g: any) => (
                          <GroupCard
                            key={g?.id}
                            group={g}
                            editMode={true}
                            setUserFields={setUserFields}
                            groups={groups}
                            setAvailableGroups={setAvailableGroups}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                          />
                        ))}
                      </Stack>
                    </Col>
                  )}
                  <Col xs={12} lg={5}>
                    <Stack gap={3} direction="horizontal" className="align-items-center">
                      <Col xs={12}>
                        <SelectObjectTypeahead
                          key={availableGroups?.length ?? 0}
                          label="Groups"
                          value={selectedGroup?.key}
                          options={availableGroups}
                          onUpdate={(e: any) => updateGroup(e)}
                        />
                      </Col>
                      <Button className="app-tertiary-color button" onClick={onClickAddGroup}>
                        Add
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </>
            )}
          </Stack>
        </div>
      )}
    </>
  );
};

export default EditMember;
